// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState, useEffect } from 'react';
import { Collapse, IconButton, Divider, ListItemButton, Typography, Box, useTheme, SvgIcon, ListItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useAppDispatch, useAppSelector } from 'Hooks';
import OrthomosaicCheckbox from './OrthomosaicCheckbox';
import stylesDef from './styles';

interface IRasterLayer {
  name: string;
  layer: any; // RasterTileLayer or custom layer object
}

const Orthomosaic = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const resources = useAppSelector((state) => state.auth.resources);
  const opsTrainer = 'opsTrainer' in resources;

  const rasterTileJson = useAppSelector((state: any) => state.map.common.rasterTileJson);
  const fileStatusJson = useAppSelector((state: any) => state.project.fileStatusJson);

  // Convert rasterTileJson object to array
  const rasterLayers: IRasterLayer[] = rasterTileJson
    ? Object.entries(rasterTileJson).map(([key, value]: [string, any]) => ({
      name: value.title || key,
      layer: value,
    }))
    : [];

  const [dataSetsExpanded, setDataSetsExpanded] = useState(true);
  const [rasterVisibility, setRasterVisibility] = useState<Record<string, boolean>>({});

  // Initialize all rasters as visible
  useEffect(() => {
    const initialVisibility: Record<string, boolean> = {};
    rasterLayers.forEach((raster) => {
      initialVisibility[raster.name] = true;
    });
    setRasterVisibility(initialVisibility);
  }, [rasterTileJson]);

  const toggleDataSetsExpanded = () => setDataSetsExpanded(!dataSetsExpanded);

  const handleMasterCheckboxChange = (allVisible: boolean) => {
    const updatedVisibility: Record<string, boolean> = {};
    rasterLayers.forEach((raster) => {
      updatedVisibility[raster.name] = allVisible;
    });
    setRasterVisibility(updatedVisibility);
  };

  // Toggle individual raster layer
  const toggleRasterVisibility = (rasterName: string) => {
    setRasterVisibility((prev) => {
      const newVisibility = !prev[rasterName];

      // Update the actual layer visibility using window.rasterLayers
      const rasterLayersList = (window as any).rasterLayers as Array<{ name: string; layer: any }>;
      if (rasterLayersList && rasterLayersList.length) {
        rasterLayersList.forEach((r) => {
          // Match by name and update visibility in all viewers
          if (r.name === rasterName && r.layer) {
            r.layer.visible = newVisibility;
          }
        });
      }

      return {
        ...prev,
        [rasterName]: newVisibility,
      };
    });
  };

  // Check if a raster has an error status
  const hasError = (rasterName: string): boolean => {
    if (!fileStatusJson) return false;

    // Check if the raster name exists in fileStatusJson and has error status
    const fileStatus = fileStatusJson[rasterName];
    return fileStatus === 'error';
  };

  return (
    <>
      <ListItemButton sx={styles.expansionPanelHeader}>
        <OrthomosaicCheckbox onMasterToggle={handleMasterCheckboxChange}
        disabled={rasterLayers.length === 0}
         />
        <IconButton
          aria-label={dataSetsExpanded ? 'Collapse' : 'Expand'}
          sx={styles.button}
          onClick={toggleDataSetsExpanded}
        >
          {dataSetsExpanded
            ? <KeyboardArrowDownIcon fontSize="small" />
            : <KeyboardArrowRightIcon fontSize="small" />}
        </IconButton>
      </ListItemButton>

      <Collapse in={dataSetsExpanded}>
        <Box sx={styles.wrapper}>
          <Box sx={styles.dataSetRow}>
            {rasterLayers.length === 0 ? (
              <Typography variant="body2" sx={styles.fileItalic}>
                No Data Available
              </Typography>
            ) : (
              rasterLayers.map((raster: IRasterLayer) => {
                const isVisible = rasterVisibility[raster.name] ?? true;
                const hasErrorStatus = hasError(raster.name);

                return (
                  <ListItem sx={styles.orthoFileListItem} key={raster.name}>
                    <Box sx={styles.orthofilelist}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                        {hasErrorStatus && (
                          <SvgIcon fontSize="small" viewBox="0 0 16 16" sx={styles.orthosvgerror}>
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M14.4001 8.0001C14.4001 11.5347 11.5347 14.4001 8.0001 14.4001C4.46548 14.4001 1.6001 11.5347 1.6001 8.0001C1.6001 4.46548 4.46548 1.6001 8.0001 1.6001C11.5347 1.6001 14.4001 4.46548 14.4001 8.0001ZM8.0001 4.0001C8.33147 4.0001 8.6001 4.26873 8.6001 4.6001V8.2001C8.6001 8.53147 8.33147 8.8001 8.0001 8.8001C7.66873 8.8001 7.4001 8.53147 7.4001 8.2001V4.6001C7.4001 4.26873 7.66873 4.0001 8.0001 4.0001ZM8.0001 12.0001C8.44193 12.0001 8.8001 11.6419 8.8001 11.2001C8.8001 10.7583 8.44193 10.4001 8.0001 10.4001C7.55827 10.4001 7.2001 10.7583 7.2001 11.2001C7.2001 11.6419 7.55827 12.0001 8.0001 12.0001Z"
                              fill="currentColor"
                            />
                          </SvgIcon>
                        )}
                        <Typography
                          variant="body2"
                          sx={styles.orthofile}
                        >
                          {raster.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRasterVisibility(raster.name);
                        }}
                        sx={styles.eyetoggleopac}
                      >
                        <SvgIcon
                          fontSize="small"
                          sx={styles.iconColor ? styles.iconColor(isVisible) : {}}
                        >
                          {isVisible ? (
                            // Eye open icon
                            <path d="M10 4.1665C5.90909 4.1665 2.41545 6.71105 1 10.3029C2.41545 13.8947 5.90909 16.4392 10 16.4392C14.0909 16.4392 17.5845 13.8947 19 10.3029C17.5845 6.71105 14.0909 4.1665 10 4.1665ZM10 14.3938C7.74182 14.3938 5.90909 12.561 5.90909 10.3029C5.90909 8.04469 7.74182 6.21196 10 6.21196C12.2582 6.21196 14.0909 8.04469 14.0909 10.3029C14.0909 12.561 12.2582 14.3938 10 14.3938ZM10 7.84832C8.64182 7.84832 7.54545 8.94469 7.54545 10.3029C7.54545 11.661 8.64182 12.7574 10 12.7574C11.3582 12.7574 12.4545 11.661 12.4545 10.3029C12.4545 8.94469 11.3582 7.84832 10 7.84832Z" />
                          ) : (
                            // Eye closed icon
                            <path d="M19.5872 1.06055L15.0764 5.57031C16.7934 6.66308 18.1433 8.283 18.8997 10.2021C17.4842 13.7939 13.9905 16.3388 9.89966 16.3389C8.27973 16.3389 6.754 15.9384 5.41333 15.2334L1.06079 19.5869L0.212158 18.7383L4.35864 14.5908C2.81491 13.5073 1.60167 11.9836 0.899658 10.2021C2.31516 6.61043 5.80882 4.06641 9.89966 4.06641C11.3582 4.06643 12.7402 4.39068 13.9797 4.96973L18.7385 0.211914L19.5872 1.06055ZM11.885 8.76172C12.1787 9.16626 12.3537 9.66269 12.3538 10.2021C12.3538 11.5603 11.2578 12.6572 9.89966 12.6572C9.36009 12.6572 8.86285 12.4822 8.45825 12.1885L7.29321 13.3535C8.00122 13.9399 8.90925 14.293 9.89966 14.293C12.1578 14.2929 13.9905 12.4603 13.9905 10.2021C13.9904 9.21179 13.6374 8.30367 13.051 7.5957L11.885 8.76172ZM9.89966 6.11133C7.64153 6.11133 5.80892 7.94404 5.80884 10.2021C5.80884 11.0367 6.0599 11.8127 6.4895 12.46L7.68677 11.2627C7.53306 10.9417 7.44458 10.5826 7.44458 10.2021C7.44467 8.84404 8.54153 7.74805 9.89966 7.74805C10.2799 7.74807 10.6383 7.83663 10.9592 7.99023L12.1575 6.79199C11.5102 6.36239 10.7342 6.11136 9.89966 6.11133Z" />
                          )}
                        </SvgIcon>
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })
            )}
          </Box>
        </Box>
      </Collapse>
      <Divider />
    </>
  );
};

export default Orthomosaic;
